/**
 * Q2 — the media inventory. READ-ONLY, AND THAT IS THE PRODUCT DECISION.
 *
 * Migration 450 gave `wedding_media` a `moderation` column and no admin
 * surface ever read it. This page reads it. It does not write it, and there is
 * no approve or hide control anywhere on it, for three reasons the copy states
 * rather than hides:
 *
 *   1. THE STATE MACHINE BELONGS TO THE COUPLE. Hiding a guest's video is
 *      their call about their own keepsake, not an operator's.
 *   2. A PHOTO OR AUDIO ROW READING 'pending' IS NOT A QUEUE. 450 added the
 *      column with DEFAULT 'pending' across every row already in the table,
 *      and nothing in the product filters photos or audio on it — those files
 *      are already in the couple's book. VIDEO is the one type the preview
 *      loader gates, out of the preview and out of the PDF, so a pending video
 *      is genuinely unseen. That is why the default view is pending video.
 *   3. THERE IS NO PLAYBACK PATH HERE. admin-backend has no signing route for
 *      a wedding media key, so an approve button would be an operator
 *      approving a clip they could not watch — the exact failure 450 argues
 *      against. `poster_key` is deliberately not projected by the server and
 *      must not be asked for: a key with no signing path behind it is a broken
 *      thumbnail that implies an affordance which does not exist.
 *
 * The server ships all three sentences (`notes.structural_pending`,
 * `notes.video_gate`, `notes.read_only`) and this page renders them VERBATIM
 * rather than paraphrasing — a paraphrase drifts, and these are exactly the
 * facts an operator would otherwise get wrong.
 *
 * ZERO HEX.
 */

import React from 'react';
import {
  Box, Card, CardContent, Chip, Link, Stack, Tooltip, Typography, Button,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import {
  DataGrid, GridColDef, GridPaginationModel, GridRenderCellParams,
} from '@mui/x-data-grid';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmptyState from '../../common/EmptyState';
import { WeddingTierChip } from '../WeddingChips';
import { weddingApi, WeddingApiError } from '../weddingApi';
import type {
  MediaModerationState, MediaResponse, MediaRow, MediaType, Numeric,
} from '../weddingApi';
import { num, relativeTime, shortDate } from '../weddingFormat';

/**
 * MIRRORS MEDIA_DEFAULT_STATE / MEDIA_DEFAULT_TYPE in weddingAdmin.js, and the
 * server ships the same pair back as `defaults` so the two can be compared.
 * They are sent EXPLICITLY rather than omitted, because the pills have to be
 * drawn before the first response arrives and a pill that guesses is worse
 * than one that states what it asked for.
 */
const DEFAULT_STATE: MediaModerationState = 'pending';
const DEFAULT_TYPE: MediaType = 'video';

type StateFilter = MediaModerationState | 'all';
type TypeFilter = MediaType | 'all';

const STATE_PILLS: { key: StateFilter; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'hidden', label: 'Hidden' },
  { key: 'all', label: 'Any state' },
];

const TYPE_PILLS: { key: TypeFilter; label: string }[] = [
  { key: 'video', label: 'Video' },
  { key: 'photo', label: 'Photo' },
  { key: 'audio', label: 'Audio' },
  { key: 'all', label: 'Any type' },
];

const TYPE_LABEL: Record<MediaType, string> = { photo: 'Photo', audio: 'Audio', video: 'Video' };

/** MUI palette keys only. */
const STATE_COLOR: Record<MediaModerationState, 'warning' | 'success' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  hidden: 'default',
};

/** Bytes → "46.0 MB". Local because nothing else in this section shows a size. */
function fileSize(bytes: Numeric | null | undefined): string {
  const n = num(bytes);
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** Seconds → "1:14". Video and audio only; a photo has no duration. */
function duration(seconds: number | null | undefined): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return '—';
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

const WeddingModerationPage: React.FC = () => {
  const [data, setData] = React.useState<MediaResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const [state, setState] = React.useState<StateFilter>(DEFAULT_STATE);
  const [type, setType] = React.useState<TypeFilter>(DEFAULT_TYPE);
  const [pagination, setPagination] = React.useState<GridPaginationModel>({ page: 0, pageSize: 25 });

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await weddingApi.getMedia({
        page: pagination.page + 1,
        limit: pagination.pageSize,
        state,
        type,
      }));
    } catch (e) {
      setError(e instanceof WeddingApiError ? e.message : 'Could not load the media inventory.');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [pagination, state, type]);

  React.useEffect(() => { load(); }, [load]);

  const changeState = (next: StateFilter) => {
    setState(next);
    setPagination((p) => ({ ...p, page: 0 }));
  };
  const changeType = (next: TypeFilter) => {
    setType(next);
    setPagination((p) => ({ ...p, page: 0 }));
  };

  const notes = data ? data.notes : null;
  const counts = data ? data.counts : [];

  const columns: GridColDef<MediaRow>[] = [
    {
      field: 'media_type',
      headerName: 'Media',
      width: 150,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MediaRow>) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Chip size="small" variant="outlined" label={TYPE_LABEL[p.row.media_type] || p.row.media_type} />
          <Chip
            size="small"
            variant="filled"
            color={STATE_COLOR[p.row.moderation] || 'default'}
            label={p.row.moderation}
          />
        </Stack>
      ),
    },
    {
      field: 'wedding_code',
      headerName: 'Book',
      flex: 1.1,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MediaRow>) => (
        <Box sx={{ py: 0.5 }}>
          {/*
            A real <a href>, not a row click: this grid has no detail dialog of
            its own and the useful next step is always the book the file
            belongs to.
          */}
          <Link
            component={RouterLink}
            to={`/dashboard/wedding/books/${p.row.book_id}`}
            variant="body2"
            underline="hover"
          >
            {p.row.wedding_code}
          </Link>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {p.row.partner1_name} &amp; {p.row.partner2_name}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'tier',
      headerName: 'Tier',
      width: 110,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MediaRow>) => <WeddingTierChip tier={p.row.tier} />,
    },
    {
      field: 'first_name',
      headerName: 'Guest',
      flex: 1.1,
      minWidth: 200,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MediaRow>) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">
            {p.row.first_name} {p.row.last_name || ''}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.relationship || p.row.guest_email}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'chapter_status',
      headerName: 'Chapter',
      width: 150,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MediaRow>) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">{p.row.chapter_status || 'no status'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {p.row.word_count === null ? 'no words yet' : `${num(p.row.word_count)} words`}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'size_bytes',
      headerName: 'File',
      width: 130,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MediaRow>) => (
        <Box sx={{ py: 0.5 }}>
          <Typography variant="body2">{fileSize(p.row.size_bytes)}</Typography>
          <Typography variant="caption" color="text.secondary">
            {duration(p.row.duration_seconds)}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Uploaded',
      width: 150,
      sortable: false,
      display: 'flex',
      renderCell: (p: GridRenderCellParams<MediaRow>) => (
        <Tooltip title={shortDate(p.row.created_at)}>
          <Typography variant="body2" color="text.secondary">
            {relativeTime(p.row.created_at)}
          </Typography>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
        <PermMediaIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Media moderation</Typography>
        <Chip size="small" variant="outlined" label="Read-only" />
        <Box sx={{ flexGrow: 1 }} />
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} size="small">
          Refresh
        </Button>
      </Stack>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Every photograph, voice note and video guests have uploaded, and the moderation state each
        one carries. Defaults to pending video — the only genuine queue.
      </Typography>

      {/*
        THE COUNTS GRID IGNORES THE FILTERS ABOVE IT, as the books pills do, so
        that filtering the list can never make the totals read as an empty
        database.
      */}
      <Card sx={{ borderRadius: '16px', mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Everything uploaded, by type and state
          </Typography>
          {counts.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No guest has uploaded anything yet.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {counts.map((c) => (
                <Chip
                  key={`${c.media_type}-${c.moderation}`}
                  size="small"
                  variant="outlined"
                  color={STATE_COLOR[c.moderation] || 'default'}
                  label={`${TYPE_LABEL[c.media_type] || c.media_type} · ${c.moderation} · ${num(c.n)}`}
                />
              ))}
            </Stack>
          )}
          {data && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1 }}>
              {data.upload_status_note}
            </Typography>
          )}
        </CardContent>
      </Card>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {STATE_PILLS.map((pill) => (
            <Chip
              key={pill.key}
              label={pill.label}
              color={state === pill.key ? 'primary' : 'default'}
              variant={state === pill.key ? 'filled' : 'outlined'}
              onClick={() => changeState(pill.key)}
              aria-pressed={state === pill.key}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {TYPE_PILLS.map((pill) => (
            <Chip
              key={pill.key}
              label={pill.label}
              color={type === pill.key ? 'secondary' : 'default'}
              variant={type === pill.key ? 'filled' : 'outlined'}
              onClick={() => changeType(pill.key)}
              aria-pressed={type === pill.key}
            />
          ))}
        </Stack>
      </Stack>

      {error && !loading ? (
        <EmptyState
          icon="error"
          title="Could not load the media inventory"
          description={error}
          action={{ label: 'Retry', onClick: load, variant: 'contained' }}
        />
      ) : (
        <DataGrid
          rows={data ? data.media : []}
          columns={columns}
          loading={loading}
          getRowId={(r) => r.id}
          rowCount={data && data.pagination ? data.pagination.total : 0}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          disableColumnMenu
          autoHeight
          localeText={{
            noRowsLabel:
              state === 'pending' && type === 'video'
                ? 'No video is waiting on the couple. Nothing is hidden from a preview or a PDF.'
                : 'No media matches these filters.',
          }}
        />
      )}

      {/*
        THE SERVER'S OWN SENTENCES, VERBATIM. All three are counter-intuitive
        enough that a paraphrase would be a new claim.
      */}
      {notes && (
        <Stack spacing={0.75} sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {notes.structural_pending}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {notes.video_gate}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {notes.read_only}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default WeddingModerationPage;
